import { Platform, StyleSheet } from 'react-native';

const cardShadow = Platform.select({
    web: { boxShadow: '0 18px 48px rgba(34, 44, 33, 0.08)' },
    default: {
        elevation: 3,
        shadowColor: '#1b281d',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.08,
        shadowRadius: 20,
    },
});

export const briefStyles = StyleSheet.create({
    heroSplit: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 64,
    },
    heroSplitStacked: {
        flexDirection: 'column',
        gap: 48,
    },
    heroCopy: {
        flex: 0.9,
        minWidth: 0,
        paddingTop: 12,
    },
    heroAside: {
        flex: 1.1,
        minWidth: 0,
        width: '100%',
    },
    // Column flex ratios would split the stacked height between copy and card.
    heroColumnStacked: {
        flexBasis: 'auto',
        flexGrow: 0,
    },
    heroKicker: {
        textAlign: 'left',
    },
    heroTitle: {
        color: '#16251a',
        fontSize: 56,
        fontWeight: '600',
        letterSpacing: -1.2,
        lineHeight: 60,
        marginTop: 22,
    },
    heroTitleNarrow: {
        fontSize: 42,
        lineHeight: 47,
    },
    heroLead: {
        color: '#425046',
        fontSize: 18,
        lineHeight: 30,
        marginTop: 24,
        maxWidth: 560,
    },
    heroAgents: {
        color: '#657167',
        fontSize: 13,
        lineHeight: 20,
        marginTop: 22,
    },
    heroActions: {
        justifyContent: 'flex-start',
        marginTop: 30,
    },

    switcherLabel: {
        color: '#477143',
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 18,
        marginBottom: 10,
        textTransform: 'uppercase',
    },
    segmented: {
        backgroundColor: '#e8e3d7',
        borderColor: '#dcd5c5',
        borderRadius: 12,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 4,
        marginBottom: 14,
        padding: 4,
    },
    segment: {
        alignItems: 'center',
        borderColor: 'transparent',
        borderRadius: 9,
        borderWidth: 1,
        flex: 1,
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'center',
        minWidth: 0,
        paddingHorizontal: 10,
        paddingVertical: 10,
    },
    segmentNarrow: {
        flexDirection: 'column',
        gap: 5,
        paddingHorizontal: 6,
    },
    segmentActive: {
        backgroundColor: '#fffdf8',
        borderColor: '#d6d0c2',
        ...Platform.select({
            web: { boxShadow: '0 2px 6px rgba(34, 44, 33, 0.12)' },
            default: {},
        }),
    },
    segmentText: {
        color: '#5d6c5b',
        fontSize: 14,
        fontWeight: '600',
    },
    segmentTextNarrow: {
        fontSize: 12,
        textAlign: 'center',
    },
    segmentTextActive: {
        color: '#16251a',
    },
    chat: {
        backgroundColor: '#fffdf8',
        borderColor: '#d6d0c2',
        borderRadius: 16,
        borderWidth: 1,
        overflow: 'hidden',
        ...cardShadow,
    },
    chatHeader: {
        alignItems: 'center',
        borderBottomColor: '#e4dfd3',
        borderBottomWidth: 1,
        flexDirection: 'row',
        gap: 10,
        paddingHorizontal: 22,
        paddingVertical: 14,
    },
    chatDot: {
        backgroundColor: '#ea580c',
        borderRadius: 999,
        height: 8,
        width: 8,
    },
    chatHeaderText: {
        color: '#2b3d2e',
        flex: 1,
        fontSize: 13,
        fontWeight: '600',
    },
    chatTime: {
        color: '#7a8279',
        fontSize: 12,
    },
    chatBody: {
        gap: 18,
        paddingHorizontal: 22,
        paddingVertical: 22,
    },
    speaker: {
        color: '#477143',
        fontSize: 11,
        fontWeight: '600',
        marginBottom: 6,
        textTransform: 'uppercase',
    },
    chatText: {
        color: '#2c3a2f',
        fontSize: 15,
        lineHeight: 24,
    },
    pick: {
        flexDirection: 'row',
        gap: 12,
        marginTop: 14,
    },
    pickNumber: {
        color: '#ea580c',
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 24,
        width: 18,
    },
    pickCopy: {
        flex: 1,
        minWidth: 0,
    },
    pickTitle: {
        color: '#1d3824',
        fontSize: 15,
        fontWeight: '600',
        lineHeight: 22,
        textDecorationLine: 'underline',
    },
    pickNote: {
        color: '#4c584f',
        fontSize: 14,
        lineHeight: 22,
        marginTop: 4,
    },
    question: {
        alignSelf: 'flex-end',
        backgroundColor: '#eaf0e4',
        borderBottomRightRadius: 4,
        borderRadius: 14,
        maxWidth: '85%',
        paddingHorizontal: 16,
        paddingVertical: 11,
    },
    sourceLink: {
        color: '#3e6d3c',
        fontSize: 13,
        fontWeight: '600',
        marginTop: 10,
        textDecorationLine: 'none',
    },
    composer: {
        alignItems: 'center',
        borderTopColor: '#e4dfd3',
        borderTopWidth: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 22,
        paddingVertical: 14,
    },
    composerText: {
        color: '#8a9188',
        fontSize: 14,
    },
    composerButton: {
        alignItems: 'center',
        backgroundColor: '#e3e7dc',
        borderRadius: 999,
        height: 28,
        justifyContent: 'center',
        width: 28,
    },
    caption: {
        color: '#6c746d',
        fontSize: 12,
        lineHeight: 18,
        marginTop: 14,
    },

    compare: {
        flexDirection: 'row',
        gap: 20,
        marginTop: 42,
    },
    compareCard: {
        borderRadius: 14,
        borderWidth: 1,
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 24,
        paddingVertical: 24,
    },
    alertCard: {
        backgroundColor: '#f4f1ea',
        borderColor: '#ddd7ca',
    },
    briefCard: {
        backgroundColor: '#fffdf8',
        borderColor: '#cfd8c8',
        borderTopColor: '#ea580c',
        borderTopWidth: 4,
    },
    compareLabel: {
        color: '#6a7368',
        fontSize: 12,
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    compareTitle: {
        color: '#1b2b1e',
        fontSize: 19,
        fontWeight: '600',
        lineHeight: 25,
        marginTop: 10,
    },
    skeletonList: {
        gap: 11,
        marginTop: 20,
    },
    skeletonRow: {
        backgroundColor: '#ddd8cc',
        borderRadius: 4,
        height: 9,
    },
    skeletonMore: {
        color: '#858c83',
        fontSize: 13,
        marginTop: 4,
    },
    briefRows: {
        marginTop: 14,
    },
    briefRow: {
        borderBottomColor: '#e4dfd3',
        borderBottomWidth: 1,
        flexDirection: 'row',
        gap: 16,
        paddingVertical: 14,
    },
    briefRowLast: {
        borderBottomWidth: 0,
    },
    briefRowCopy: {
        flex: 1,
        minWidth: 0,
    },
    briefRowMark: {
        color: '#ea580c',
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 22,
        width: 20,
    },
    briefRowTitle: {
        color: '#1d3824',
        fontSize: 15,
        fontWeight: '600',
        lineHeight: 22,
    },
    briefRowBody: {
        color: '#59635a',
        fontSize: 14,
        lineHeight: 21,
        marginTop: 2,
    },
    compareCaption: {
        color: '#59635a',
        fontSize: 14,
        lineHeight: 22,
        marginTop: 18,
    },

    cardRow: {
        flexDirection: 'row',
        gap: 16,
        marginTop: 42,
    },
    dayCard: {
        backgroundColor: '#fffdf8',
        borderColor: '#cfd8c8',
        borderRadius: 14,
        borderWidth: 1,
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 20,
        paddingVertical: 22,
    },
    dayQuiet: {
        backgroundColor: 'transparent',
        borderStyle: 'dashed',
    },
    dayLabel: {
        color: '#ea580c',
        fontSize: 12,
        fontWeight: '600',
        textTransform: 'uppercase',
    },

    flow: {
        alignItems: 'stretch',
        flexDirection: 'row',
        gap: 10,
        marginTop: 42,
    },
    flowStacked: {
        flexDirection: 'column',
    },
    flowNode: {
        backgroundColor: '#fffdf8',
        borderColor: '#cfd8c8',
        borderRadius: 14,
        borderWidth: 1,
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 18,
        paddingVertical: 20,
    },
    flowNodeAccent: {
        borderColor: '#ea580c',
        borderWidth: 2,
    },
    flowArrow: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    flowLabel: {
        color: '#477143',
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    flowTitle: {
        color: '#1b2b1e',
        fontSize: 17,
        fontWeight: '600',
        lineHeight: 23,
        marginTop: 10,
    },
    flowBody: {
        color: '#59635a',
        fontSize: 14,
        lineHeight: 21,
        marginTop: 6,
    },
    noteRow: {
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 14,
        marginTop: 26,
    },
    noteText: {
        color: '#59635a',
        fontSize: 14,
        lineHeight: 22,
    },
    inlineLink: {
        color: '#3e6d3c',
        fontSize: 14,
        fontWeight: '600',
        lineHeight: 22,
        textDecorationLine: 'none',
    },

    assistants: {
        borderTopColor: '#d9d4c8',
        borderTopWidth: 1,
        flexDirection: 'row',
        gap: 24,
        marginTop: 48,
        paddingTop: 30,
    },
    assistant: {
        flex: 1,
        minWidth: 0,
    },
    assistantName: {
        color: '#1b2b1e',
        fontSize: 17,
        fontWeight: '600',
        lineHeight: 23,
    },
    assistantStatus: {
        color: '#477143',
        fontSize: 11,
        fontWeight: '600',
        marginTop: 4,
        textTransform: 'uppercase',
    },
    assistantBody: {
        color: '#59635a',
        fontSize: 14,
        lineHeight: 21,
        marginTop: 8,
    },
    promptLabelStacked: {
        flexBasis: 'auto',
    },
    faqLink: {
        marginTop: 10,
    },
});
